/**
 * The Enrollments roster's filter vocabulary, shared by the roster read
 * (`GET /admin/enrollments/roster`) and the page's menus so the two cannot
 * drift.
 */

/** Enrollment statuses the Status menu can narrow the roster to. */
export const ENROLLMENT_ROSTER_STATUSES = [
  'PENDING_PAYMENT',
  'PAYMENT_SUBMITTED',
  'VERIFIED_ACTIVE',
  'ON_HOLD',
  'FORMER',
  'REJECTED',
] as const;

export type EnrollmentRosterStatus =
  (typeof ENROLLMENT_ROSTER_STATUSES)[number];

/**
 * The Status menu. `current`, the default, is applicants, enrolled and on-hold
 * students, plus anyone whose money is unresolved whatever their status: money
 * never hides behind a filter. A status narrows to that status, settled rows
 * included; `all` is everyone.
 */
export const ENROLLMENT_ROSTER_STATUS_FILTERS = [
  'current',
  ...ENROLLMENT_ROSTER_STATUSES,
  'all',
] as const;

export type EnrollmentRosterStatusFilter =
  (typeof ENROLLMENT_ROSTER_STATUS_FILTERS)[number];

/**
 * The Billing menu. Each value selects the students wearing that money state
 * on any of their class lines: `owes` is unpaid lessons or a payment due,
 * `needs_checking` a balance or payment to check, `paid_ahead` paid lessons
 * still to use (low credits included), `settled` a class left with nothing
 * outstanding, and `no_class` a verified student with no class.
 */
export const ENROLLMENT_ROSTER_BILLING_FILTERS = [
  'owes',
  'needs_checking',
  'paid_ahead',
  'settled',
  'no_class',
] as const;

export type EnrollmentRosterBillingFilter =
  (typeof ENROLLMENT_ROSTER_BILLING_FILTERS)[number];

/**
 * The Sort menu. `chase`, the default, is the roster's own order: worst
 * first by chase tier, name within a tier. `newest` and `oldest` run by when
 * the registration form came in, a participant with no form (a logged data
 * error) last either way; `name` is A–Z by the name the row shows.
 */
export const ENROLLMENT_ROSTER_SORTS = [
  'chase',
  'newest',
  'oldest',
  'name',
] as const;

export type EnrollmentRosterSort = (typeof ENROLLMENT_ROSTER_SORTS)[number];
